import React, { useContext, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useFormState } from 'react-final-form';
import styled, { css } from 'styled-components';
import moment from 'moment';
import {
  Chip,
  ConfirmationModal,
  bodySemiBold,
  smallBody,
  smallBodySemiBold,
  extraSmall,
  CircleIcon
} from '@ubeya/shared-web/components';
import { ReactComponent as ArrowUpIcon } from '@ubeya/shared-web/assets/arrow-up.svg';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { HIRING_APPLICATION_STATUS } from '@ubeya/shared/constants/hiring';
import getStageName from 'hiring/app/hiring/openingDetail/getStageName';

const STATUS_STYLES = {
  [HIRING_APPLICATION_STATUS.IN_PROGRESS]: { labelKey: 'inProgress', background: 'orangeLight', color: 'orangeDark' },
  [HIRING_APPLICATION_STATUS.HIRED]: {
    labelKey: 'stageHiredName',
    background: 'greenExtraLight',
    color: 'greenPositive'
  },
  [HIRING_APPLICATION_STATUS.REJECTED]: {
    labelKey: 'notMovingForwardFilter',
    background: 'redExtraLight',
    color: 'redNegative'
  }
};

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;

  ${({ $isOpen, theme }) =>
    $isOpen &&
    css`
      border-bottom: 1px solid ${theme.colors.grey2};
    `}
`;

const OpeningLink = styled(Link)`
  ${bodySemiBold};
  color: ${({ theme }) => theme.colors.black};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
  }
`;

const Muted = styled.div`
  ${smallBody};
  color: ${({ theme }) => theme.colors.grey4};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
`;

const StatusPill = styled(Chip)`
  padding: 2px 10px;
  border-radius: 30px;
  white-space: nowrap;
  background: ${({ theme, $statusStyle }) => theme.colors[$statusStyle.background]};
  color: ${({ theme, $statusStyle }) => theme.colors[$statusStyle.color]};
`;

const ToggleIcon = styled(CircleIcon)`
  svg {
    transition: transform 0.2s;
    ${({ $isOpen }) => !$isOpen && 'transform: rotate(180deg);'}
  }
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px 16px;
`;

const Summary = styled.div`
  ${smallBody};
  color: ${({ theme }) => theme.colors.grey4};
`;

const SummaryValue = styled.span`
  ${smallBodySemiBold};
  color: ${({ theme, $colorKey }) => theme.colors[$colorKey ?? 'black']};
`;

const StageName = styled.div`
  ${smallBody};
  color: ${({ theme }) => theme.colors.black};
`;

const StageDate = styled.div`
  ${extraSmall};
  color: ${({ theme }) => theme.colors.grey4};
`;

const CommentsTitle = styled.div`
  ${smallBody};
  color: ${({ theme }) => theme.colors.black};
`;

const CommentBox = styled.div`
  ${smallBody};
  margin-top: 6px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.grey4};
  white-space: pre-wrap;
`;

const isNewTabClick = (e) => e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0;

const HiringApplicationCard = ({ application, accountId, onNavigate }) => {
  const { t, dateFormat, timeFormat } = useContext(ConfigContext);
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(true);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
  // Rendered inside the employee card's form, whose own close asks before dropping unsaved edits
  const { pristine } = useFormState({ subscription: { pristine: true } });

  const {
    applicationId,
    openingId,
    openingName,
    status,
    appliedAt,
    hiredAt,
    rejectedAt,
    rejectionReason
  } = application;
  const statusStyle = STATUS_STYLES[status] ?? STATUS_STYLES[HIRING_APPLICATION_STATUS.IN_PROGRESS];
  const currentStageName = application.currentStage ? getStageName(application.currentStage, t) : '';
  const openingPath = `/${accountId}/hiring/${openingId}?applicationId=${applicationId}`;

  // A new-tab click leaves this card open; a plain one closes it, so it asks first like the card's own close
  const handleOpeningClick = (e) => {
    if (isNewTabClick(e)) return;
    if (!pristine) {
      e.preventDefault();
      setIsDiscardConfirmOpen(true);
      return;
    }
    onNavigate();
  };

  const discardAndNavigate = () => {
    onNavigate();
    history.push(openingPath);
  };

  return (
    <Card data-testid={`employee-hiring-application-${applicationId}`}>
      <CardHeader $isOpen={isOpen}>
        <div>
          <OpeningLink to={openingPath} onClick={handleOpeningClick}>
            {openingName}
          </OpeningLink>
          <Muted>{t('hiringAppliedOn', { date: moment(appliedAt).format(dateFormat) })}</Muted>
        </div>

        <HeaderActions>
          <StatusPill $statusStyle={statusStyle} label={t(statusStyle.labelKey)} />
          <ToggleIcon icon={ArrowUpIcon} $isOpen={isOpen} onClick={() => setIsOpen((open) => !open)} />
        </HeaderActions>
      </CardHeader>

      {isOpen && (
        <CardBody>
          {status === HIRING_APPLICATION_STATUS.IN_PROGRESS && (
            <Summary>
              {t('hiringCurrentStage')} <SummaryValue>{currentStageName}</SummaryValue>
            </Summary>
          )}
          {status === HIRING_APPLICATION_STATUS.HIRED && (
            <Summary>
              {t('hiringOutcome')} <SummaryValue $colorKey="greenPositive">{t('stageHiredName')}</SummaryValue>{' '}
              {hiredAt && moment(hiredAt).format(dateFormat)}
            </Summary>
          )}
          {status === HIRING_APPLICATION_STATUS.REJECTED && (
            <Summary>
              {t('hiringOutcome')} <SummaryValue $colorKey="redNegative">{t('notMovingForwardFilter')}</SummaryValue>{' '}
              {rejectedAt && moment(rejectedAt).format(dateFormat)}
            </Summary>
          )}

          {(application.stages ?? []).map(({ stage, enteredAt }, index) => (
            <div key={`${stage.id ?? stage.name}-${enteredAt}-${index}`}>
              <StageName>{getStageName(stage, t)}</StageName>
              <StageDate>{moment(enteredAt).format(`${dateFormat} · ${timeFormat}`)}</StageDate>
            </div>
          ))}

          {status === HIRING_APPLICATION_STATUS.REJECTED && (
            <div>
              <CommentsTitle>{t('comments')}</CommentsTitle>
              <CommentBox>
                {t('hiringStoppedAt', {
                  stage: currentStageName,
                  date: rejectedAt ? moment(rejectedAt).format(dateFormat) : ''
                })}
                {rejectionReason && `\n${rejectionReason}`}
              </CommentBox>
            </div>
          )}
        </CardBody>
      )}

      {isDiscardConfirmOpen && (
        <ConfirmationModal
          usePortal
          title="discardYourChanges"
          body="unsavedChangesCloseBody"
          confirmText="discardChanges"
          cancelText="keepEditing"
          onConfirm={discardAndNavigate}
          onClose={() => setIsDiscardConfirmOpen(false)}
        />
      )}
    </Card>
  );
};

export default HiringApplicationCard;
